import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getLiveUserActivityOverview } from '../services/engagementService';
import '../styles/liveUserActivityAdmin.css';

const WINDOWS = [
  ['live', 'Anlık', 'Live'],
  ['1', '1 saat', '1 hour'],
  ['3', '3 saat', '3 hours'],
  ['6', '6 saat', '6 hours'],
  ['12', '12 saat', '12 hours'],
  ['24', '24 saat', '24 hours'],
];

const moduleLabels = {
  family_setup: ['Aile', 'Family'], shopping: ['Alışveriş', 'Shopping'], calendar: ['Takvim', 'Calendar'],
  finance: ['Finans', 'Finance'], expenses: ['Harcamalar', 'Expenses'], tasks: ['Görevler', 'Tasks'],
  health: ['Sağlık', 'Health'], map: ['Harita', 'Map'], children: ['Çocuk takibi', 'Children'],
  chat: ['Sohbet', 'Chat'], documents: ['Belgeler', 'Documents'], cooking: ['Yemek', 'Cooking'],
};

function relativeTime(value, tr) {
  const diff = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return tr ? 'Az önce' : 'Just now';
  if (minutes < 60) return tr ? `${minutes} dk önce` : `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return tr ? `${hours} sa önce` : `${hours} hr ago`;
}

function Distribution({ title, rows = [] }) {
  return (
    <div className="liveAdminDistribution">
      <strong>{title}</strong>
      <div>{rows.length ? rows.map((row) => (
        <span key={row.label}><b>{row.label || '-'}</b><em>{row.count || 0}</em></span>
      )) : <small>—</small>}</div>
    </div>
  );
}

export default function LiveUserActivityAdmin({ tr = true, onLiveCountChange }) {
  const [selectedWindow, setSelectedWindow] = useState('live');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  const load = useCallback(async (windowKey = selectedWindow) => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setError('');
    try {
      const result = await getLiveUserActivityOverview(windowKey);
      if (requestId.current === id) {
        setOverview(result);
        onLiveCountChange?.(Number(result?.windows?.live || 0));
      }
    } catch (e) {
      if (requestId.current === id) setError(e?.message || (tr ? 'Aktivite verileri alınamadı.' : 'Activity data could not be loaded.'));
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  }, [selectedWindow, tr, onLiveCountChange]);

  useEffect(() => { load('live'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectWindow = (key) => {
    if (key === selectedWindow && overview) return;
    setSelectedWindow(key);
    load(key);
  };
  const d = overview?.distributions || {};
  const x = overview?.extra || {};

  return (
    <div className="liveUserActivityAdmin">
      <div className="liveAdminToolbar">
        <div>
          <strong>{tr ? 'Aktivite zaman aralığı' : 'Activity window'}</strong>
          <small>{overview?.generatedAt ? `${tr ? 'Son güncelleme' : 'Last updated'}: ${new Date(overview.generatedAt).toLocaleTimeString(tr ? 'tr-TR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}` : ''}</small>
        </div>
        <button type="button" onClick={() => load(selectedWindow)} disabled={loading}>
          <span aria-hidden="true">↻</span>{loading ? (tr ? 'Yükleniyor' : 'Loading') : (tr ? 'Yenile' : 'Refresh')}
        </button>
      </div>

      <div className="liveAdminWindows">
        {WINDOWS.map(([key, trLabel, enLabel]) => (
          <button type="button" key={key} className={selectedWindow === key ? 'active' : ''} onClick={() => selectWindow(key)}>
            <strong>{overview?.windows?.[key] ?? '—'}</strong>
            <span>{tr ? trLabel : enLabel}</span>
          </button>
        ))}
      </div>

      {error && <div className="liveAdminError">{error}</div>}

      <div className="liveAdminInsightGrid">
        <div><strong>{x.newUsersToday ?? x.newUsers24h ?? '—'}</strong><span>{tr ? 'Yeni kullanıcı · Bugün' : 'New users · Today'}</span></div>
        <div><strong>{x.premiumToday ?? x.premium24h ?? '—'}</strong><span>{tr ? 'Premium aktif · Bugün' : 'Premium active · Today'}</span></div>
        <div><strong>{x.pushReadyToday ?? x.pushReady24h ?? '—'}</strong><span>{tr ? 'Push hazır · Bugün' : 'Push ready · Today'}</span></div>
        <div><strong>{x.inactive7d ?? '—'}</strong><span>{tr ? '7+ gün pasif' : 'Inactive 7+ days'}</span></div>
        <div><strong>{x.inactive30d ?? '—'}</strong><span>{tr ? '30+ gün pasif' : 'Inactive 30+ days'}</span></div>
        <div><strong>{x.trackedUsers ?? '—'}</strong><span>{tr ? 'Takip edilen toplam' : 'Total tracked'}</span></div>
      </div>

      <div className="liveAdminDistributionGrid">
        <Distribution title={tr ? 'Platform · Bugün' : 'Platform · Today'} rows={d.platforms} />
        <Distribution title={tr ? 'Dil · Bugün' : 'Language · Today'} rows={d.languages} />
        <Distribution title={tr ? 'Sürüm · Bugün' : 'Version · Today'} rows={d.versions} />
        <Distribution title={tr ? 'Popüler modüller · Bugün' : 'Popular modules · Today'} rows={(d.modules || []).map((row) => ({ ...row, label: moduleLabels[row.label]?.[tr ? 0 : 1] || row.label }))} />
      </div>

      <div className="liveAdminUserSection">
        <div className="liveAdminUserSectionTitle">
          <strong>{tr ? 'Kullanıcılar' : 'Users'} · {overview?.selectedCount ?? 0}</strong>
          {(overview?.selectedCount || 0) > (overview?.listLimit || 250) && <small>{tr ? `İlk ${overview.listLimit} kayıt` : `First ${overview.listLimit} records`}</small>}
        </div>
        <div className="liveAdminUserList">
          {!loading && !(overview?.users || []).length && <div className="liveAdminEmpty">{tr ? 'Bu aralıkta aktif kullanıcı yok.' : 'No active users in this window.'}</div>}
          {(overview?.users || []).map((user) => (
            <div className="liveAdminUser" key={user.id}>
              <span className="liveAdminAvatar">{String(user.displayName || '?').trim().charAt(0).toUpperCase()}</span>
              <div className="liveAdminUserMain">
                <strong>{user.displayName}</strong>
                <span>{user.familyName || (tr ? 'Aile yok' : 'No family')} · {moduleLabels[user.lastModule]?.[tr ? 0 : 1] || user.lastModule}</span>
              </div>
              <div className="liveAdminUserTags">
                <span>{String(user.platform || 'unknown').toUpperCase()}</span>
                <span>v{user.appVersion || '-'}</span>
                <span>{String(user.language || 'tr').toUpperCase()}</span>
                {user.isPremium && <span className="premium">Premium</span>}
                <span className={user.hasActivePush ? 'pushOn' : 'pushOff'}>{user.hasActivePush ? 'Push ✓' : 'Push —'}</span>
              </div>
              <time>{relativeTime(user.lastSeenAt, tr)}</time>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
